import React, { useEffect, useCallback } from 'react';
import { Col, Row, theme, Input } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import TextBox from 'components/TextBox';
import CustomSelect from 'components/CustomSelect';
import CustomTable from '../CustomTable';
import NestedTable from '../NestedTable';
import GapsAuditFindingsTable from '../GapsAuditFindingsTable';
import { gapsOp } from 'store/ducks/Gaps';
import { IAuditData, IAudits } from 'store/ducks/Gaps/types';
import { AuditFindingColumns } from 'constants/tableColumns';
import * as Styled from './AuditFindingsTable.styled';
import Helpers from 'utils/Helpers';

const { useToken } = theme;

// Component for displaying audit findings mapping with expandable rows
const AuditFindingsTable: React.FC = () => {
  const dispatch = useDispatch();
  const { token } = useToken();

  const { TextArea } = Input;
  // Get audit finding table columns configuration
  const auditFindingColumns = AuditFindingColumns();

  // Select required state from Redux store
  const {
    lang,
    drawTable,
    auditFindingMapping,
    assignedAuditFindingsUpdated,
    loading,
    scheme,
    orgContext
  } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    drawTable: state.complianceEst.drawTable,
    auditFindingMapping: state.gaps.auditFindingMapping,
    assignedAuditFindingsUpdated: state.gaps.assignedAuditFindingsUpdated,
    loading: state.gaps.loading,
    scheme: state.global.scheme,
    orgContext: state.global.hierarchicalLevelsScope
  }));

  const filterParams = Helpers.toApiFilters(orgContext);

  // Deletes an audit finding and refreshes the data
  const fetchDeleteAudit = async (audit: IAudits) => {
    dispatch(gapsOp.setAssignedAuditFindingsUpdated(false));
    try {
      const params = { afg_id: audit.afg_id };
      await gapsOp.deleteAudits(params);
      dispatch(gapsOp.setAssignedAuditFindingsUpdated(true));
    } catch (error) {
      console.error('Failed to fetch audits:', error);
    }
  };

  // Process API response and filter out empty rows
  const dataSource = (() => {
    try {
      if (
        !auditFindingMapping ||
        !Array.isArray(auditFindingMapping) ||
        auditFindingMapping.length === 0
      ) {
        return [];
      }

      // Handle the data directly as it's now properly formatted
      const rawData = auditFindingMapping;

      // Filter out empty or invalid rows
      const filtered = Array.isArray(rawData)
        ? rawData.filter(
            (item: { id?: number; value?: string; AUDITS?: IAudits[] }) => {
              return (
                item &&
                typeof item === 'object' &&
                (item.id ||
                  item.value ||
                  (item.AUDITS &&
                    Array.isArray(item.AUDITS) &&
                    item.AUDITS.length > 0))
              );
            }
          )
        : [];

      return filtered;
    } catch (error) {
      return [];
    }
  })();

  // Renders expanded row content with nested table
  const expandedRow = useCallback(
    (key: string) => {
      if (!dataSource || !Array.isArray(dataSource)) return null;
      const record = dataSource.find((item: IAuditData) => item.id === key);

      if (!record) return null;
      // Define columns for the nested table
      const nestedTableColumns = [
        {
          title: 'Regulatory Requirements',
          dataIndex: 'value',
          key: 'value',
          width: 300,
          render: (value: string) => (
            <Styled.ClientReqTextArea
              value={value}
              rows={3}
              level={record?.level}
              width={300}
              readOnly={false}
              scheme={scheme}
            />
          )
        },
        {
          title: 'Category',
          dataIndex: 'category',
          key: 'category',
          width: 100,
          render: (category: string) => {
            const hasAudits =
              record.AUDITS &&
              Array.isArray(record.AUDITS) &&
              record.AUDITS.length > 0;
            return (
              <Styled.CategoryContainer hasAudits={hasAudits}>
                <CustomSelect options={[]} defaultValue={category} disabled />
              </Styled.CategoryContainer>
            );
          }
        },
        {
          title: 'Aligned Audit findings',
          dataIndex: 'AUDITS',
          key: 'AUDITS',
          width: 676,
          render: (audits: IAudits[]) => (
            <Styled.AlignedAuditFindingsContainer>
              <Styled.AuditsContainer token={token}>
                {audits && Array.isArray(audits) && audits.length ? (
                  audits.map((audit: IAudits) => (
                    <Styled.StyledAssignedFindings key={audit.afg_id}>
                      <TextBox width={676}>{audit.afg_desc}</TextBox>
                      <Styled.StyledDeleteDiv>
                        <Styled.StyledButton
                          type='primary'
                          token={token}
                          icon={<DeleteOutlined />}
                          onClick={() => fetchDeleteAudit(audit)}
                        />
                      </Styled.StyledDeleteDiv>
                    </Styled.StyledAssignedFindings>
                  ))
                ) : (
                  <Styled.StyledTextBox width={676} token={token} />
                )}
              </Styled.AuditsContainer>
            </Styled.AlignedAuditFindingsContainer>
          )
        }
      ];
      // Filter data for the specific row
      const filteredDataSource = dataSource.filter(
        (item: IAuditData) => item.id === key
      );

      return (
        <NestedTable
          className='nested-table'
          columns={nestedTableColumns}
          dataSource={filteredDataSource.map((item: IAuditData) => ({
            ...item,
            key: item.id
          }))}
          pagination={false}
        />
      );
    },
    [dataSource, token, fetchDeleteAudit]
  );

  // Fetch data when component mounts or when drawTable becomes true

  useEffect(() => {
    const hasFilters = Object.keys(filterParams).length > 0;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!hasFilters) return;

    if (drawTable) {
      gapsOp
        .fetchAuditFindingsMapping(lang, filterParams)
        .then((response) => {
          dispatch(gapsOp.setAuditFindingsMapping(response));
        })
        .catch((error) => {
          console.error('Failed to fetch audit findings mapping:', error);
        });
    }
  }, [lang, dispatch, drawTable]);

  // Refetch data when assigned audit findings are updated
  useEffect(() => {
    const hasFilters = Object.keys(filterParams).length > 0;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!hasFilters) return;

    if (assignedAuditFindingsUpdated) {
      gapsOp
        .fetchAuditFindingsMapping(lang, filterParams)
        .then((response) => {
          dispatch(gapsOp.setAuditFindingsMapping(response));
        })
        .catch((error) => {
          console.error('Failed to fetch audit findings mapping:', error);
        });
    }
    dispatch(gapsOp.setAssignedAuditFindingsUpdated(false));
  }, [assignedAuditFindingsUpdated, dispatch, lang]);

  return (
    <Row>
      <>
        <Col lg={12}>
          <Styled.StyledCustomTable
            drawTable={drawTable}
            token={token}
            columns={auditFindingColumns}
            expandable={{
              expandedRowKeys:
                dataSource && Array.isArray(dataSource)
                  ? dataSource.map((item: IAuditData) => item?.id || 0)
                  : [],
              expandedRowRender: (record: IAuditData) => expandedRow(record?.id)
            }}
            dataSource={
              dataSource && Array.isArray(dataSource)
                ? dataSource.map((item: IAuditData) => ({
                    ...item,
                    key: item?.id || 0
                  }))
                : []
            }
            itemsCount={
              dataSource && Array.isArray(dataSource) ? dataSource.length : 0
            }
            loading={loading}
          />
        </Col>
        <Col lg={12}>
          <GapsAuditFindingsTable />
        </Col>
      </>
    </Row>
  );
};

export default AuditFindingsTable;
